import { toPng } from "html-to-image";
import moment from "moment";
import React, { useEffect, useState } from "react";
import axiosInstance from "../lib/axios";

const MasterCardSuccess = (props) => {
    const elementRef = React.useRef(null);
    const [status, setStatus] = useState("loading");
    const [details, setDetails] = useState(null);
    const [message, setMessage] = useState("");

    // Get reference from props or query string
    const getReference = () => {
        if (props.reference) return props.reference;
        const params = new URLSearchParams(window.location.search);
        return params.get("ref") || "";
    };

    const getTotalAmount = () => {
        if (props.amount) return props.amount;
        const params = new URLSearchParams(window.location.search);
        return params.get("amt") || 0;
    };

    const getSuccessUrl = () => {
        if (props.redirectWhenSuccessfulUrl) return props.redirectWhenSuccessfulUrl;
        const params = new URLSearchParams(window.location.search);
        return params.get("redirectWhenSuccessfulUrl") || '';
    };

    const getFailureUrl = () => {
        if (props.redirectWhenCancelledUrl) return props.redirectWhenCancelledUrl;
        const params = new URLSearchParams(window.location.search);
        return params.get("redirectWhenCancelledUrl") || '';
    };

    const reference = getReference();
    const totalAmount = getTotalAmount();
    const redirectWhenSuccessfulUrl = getSuccessUrl();
    const redirectWhenCancelledUrl = getFailureUrl();

    const confirmTransaction = async () => {
        setStatus("loading");
        try {
            const response = await axiosInstance.get(
                `https://api.xcelapp.com/transactions-service/mastercard/status/${reference}`,
            );
            const res = response?.data;
            setDetails(res || {});
            setMessage(res.message || "");
            if (res.isSuccess) {
                setStatus("success");
            } else if (res.isFailure) {
                setStatus("failure");
            } else if (res.isPending) {
                setStatus("pending");
            } else {
                setStatus("failure"); 
            }
        } catch (err) {
            setMessage(err?.response?.message || err.message || "Payment failed");
            setStatus("failure");
        }
    };

    useEffect(() => {
        if (reference) confirmTransaction();
        else setStatus("failure");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reference]);

    const normalizeUrl = (url) => {
        try {
            if (url.includes('%')) {
                const decoded = decodeURIComponent(url);
                return decoded;
            }
            return url;
        } catch (error) {
            console.warn("Invalid URL passed to normalizeUrl:", url);
            return url;
        }
    };

    // Render loading or pending state
    if (status === "loading") {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-center my-5">Confirming your payment...</p>
            </div>
        );
    }

    // Render failure state
    if (status === "failure") {
        return (
            <div ref={elementRef} className="mt-20 container">
                <div className="receipt-modal text-center">
                    <div className="pt-10 flex justify-center">
                        <svg width="67" height="67" viewBox="0 0 67 67" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="33.5" cy="33.5" r="29.5" fill="#FFEBEB" stroke="#FF4D4F" strokeWidth="8" />
                            <path d="M22 22L45 45" stroke="#FF4D4F" strokeWidth="4" strokeLinecap="round" />
                            <path d="M45 22L22 45" stroke="#FF4D4F" strokeWidth="4" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h3 className="my-3 text-center text-red-600">Payment Failed</h3>
                    <p className="mb-2 text-center text-lg text-gray-700">{message || "Your payment could not be confirmed."}</p>
                    
                    <div className="main">
                        <table>
                            <tr className="px-3">
                                <td>Transaction Type</td>
                                <td className="font-semibold">{"Card"}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Transaction Reference</td>
                                <td className="font-semibold">{reference || 'N/A'}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Total Amount</td>
                                <td className="font-semibold">{"GHS"} {Number(totalAmount || 0).toFixed(2)}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Status</td>
                                <td className="text-red-500 font-semibold">FAILED</td>
                            </tr>
                            <tr className="px-3">
                                <td>Date</td>
                                <td className="font-semibold">{details?.timestamp || moment().format("YYYY-MM-DD")}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Time</td>
                                <td className="font-semibold">{details?.timestamp || moment().format("hh:mm A")}</td>
                            </tr>
                        </table>
                    </div>

                    <div className="no-print">
                        <div className="no-print px-4">
                            <button
                                className="mt-4 bg-[#0095FF] hover:bg-[#0095FFb6] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                                type="button"
                                onClick={() => {
                                    toPng(elementRef.current, { cacheBust: false })
                                        .then((dataUrl) => {
                                            const link = document.createElement("a");
                                            link.download = "receipt.png";
                                            link.href = dataUrl;
                                            link.click();
                                        })
                                        .catch((err) => {});
                                }}>
                                Download Receipt
                            </button>
                        </div>
                        <div className="footer">
                            <a
                                className="bg-[#fff] hover:bg-[#fff] border-[#979797] border-[1px] text-[#979797] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full text-center no-underline"
                                type="button"
                                href={
                                    redirectWhenCancelledUrl
                                        ? normalizeUrl(redirectWhenCancelledUrl)
                                        : 'https://xcelapp.com/'
                                }
                            >
                                Close
                            </a>
                            <button
                                className="bg-[#F3F3F3] text-[#353535] border border-[#353535] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                                type="button"
                                onClick={confirmTransaction}
                            >
                                Recheck Status
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === "pending") {
        return (
            <div ref={elementRef} className="mt-20 container">
                <div className="receipt-modal text-center">
                    <div className="pt-10 flex justify-center">
                        <svg width="67" height="67" viewBox="0 0 67 67" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="33.5" cy="33.5" r="29.5" fill="#FFEBEB" stroke="#d4af36" strokeWidth="8" />
                            <path d="M22 22L45 45" stroke="#d4af36" strokeWidth="4" strokeLinecap="round" />
                            <path d="M45 22L22 45" stroke="#d4af36" strokeWidth="4" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h3 className="my-3 text-center text-[#d4af36]">Payment Pending</h3>
                    <p className="my-6 text-center text-lg text-gray-700">{message || "Your payment could not be confirmed."}</p>
                    
                    
                    <div className="main">
                        <table>
                            <tr className="px-3">
                                <td>Transaction Type</td>
                                <td className="font-semibold">{"Card"}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Transaction Reference</td>
                                <td className="font-semibold">{reference || 'N/A'}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Total Amount</td>
                                <td className="font-semibold">{"GHS"} {Number(totalAmount || 0).toFixed(2)}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Status</td>
                                <td className="text-[#d4af36] font-semibold">PENDING</td>
                            </tr>
                            <tr className="px-3">
                                <td>Date</td>
                                <td className="font-semibold">{details?.timestamp || moment().format("YYYY-MM-DD")}</td>
                            </tr>
                            <tr className="px-3">
                                <td>Time</td>
                                <td className="font-semibold">{details?.timestamp || moment().format("hh:mm A")}</td>
                            </tr>
                        </table>
                    </div>

                    <div>
                        <div className="footer">
                            <a
                                className="bg-[#fff] hover:bg-[#fff] border-[#979797] border-[1px] text-[#979797] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full text-center no-underline"
                                type="button"
                                href={
                                    redirectWhenSuccessfulUrl
                                        ? normalizeUrl(redirectWhenSuccessfulUrl)
                                        : 'https://xcelapp.com/'
                                }
                            >
                                Close
                            </a>
                            <button
                                className="bg-[#0095FF] hover:bg-[#0095FFb6] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                                type="button"
                                onClick={() => {
                                    toPng(elementRef.current, { cacheBust: false })
                                        .then((dataUrl) => {
                                            const link = document.createElement("a");
                                            link.download = "receipt.png";
                                            link.href = dataUrl;
                                            link.click();
                                        })
                                        .catch((err) => {});
                                }}>
                                Download Receipt
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Render success state
    return (
        <div ref={elementRef} className="mt-20 container">
            <div className="receipt-modal">
                <div className="">
                    <div className="pt-10 flex justify-center">
                        <svg width="67" height="67" viewBox="0 0 67 67" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M33.5 4.1875C41.2742 4.1875 48.7299 7.27577 54.2271 12.7729C59.7242 18.2701 62.8125 25.7258 62.8125 33.5C62.8125 41.2742 59.7242 48.7299 54.2271 54.2271C48.7299 59.7242 41.2742 62.8125 33.5 62.8125C25.7258 62.8125 18.2701 59.7242 12.7729 54.2271C7.27577 48.7299 4.1875 41.2742 4.1875 33.5C4.1875 25.7258 7.27577 18.2701 12.7729 12.7729C18.2701 7.27577 25.7258 4.1875 33.5 4.1875ZM29.8485 39.2829L23.3369 32.7672C23.1035 32.5338 22.8264 32.3486 22.5214 32.2222C22.2164 32.0959 21.8895 32.0309 21.5593 32.0309C21.2292 32.0309 20.9023 32.0959 20.5973 32.2222C20.2923 32.3486 20.0152 32.5338 19.7817 32.7672C19.3103 33.2386 19.0454 33.8781 19.0454 34.5448C19.0454 35.2115 19.3103 35.8509 19.7817 36.3224L28.073 44.6136C28.3058 44.8482 28.5827 45.0344 28.8878 45.1615C29.1929 45.2886 29.5201 45.354 29.8506 45.354C30.1811 45.354 30.5083 45.2886 30.8134 45.1615C31.1185 45.0344 31.3954 44.8482 31.6282 44.6136L48.7969 27.4407C49.0335 27.2082 49.2217 26.9312 49.3507 26.6257C49.4796 26.3201 49.5468 25.992 49.5483 25.6604C49.5499 25.3287 49.4858 25 49.3596 24.6933C49.2335 24.3865 49.0479 24.1078 48.8135 23.8731C48.5791 23.6385 48.3006 23.4525 47.994 23.326C47.6874 23.1996 47.3588 23.135 47.0272 23.1362C46.6955 23.1373 46.3673 23.2041 46.0616 23.3328C45.7559 23.4614 45.4787 23.6492 45.2459 23.8855L29.8485 39.2829Z" fill="#1EC66E"/>
                        </svg>
                    </div>
                    <h3 className="my-4 text-center">
                        Congratulations
                    </h3>
                    <p className="my-4 text-center">
                        <span className="mb-4 text-lg font-semibold">
                            {message || "Your payment was successful"}
                        </span>
                        <br />
                        Your payment of {" "}
                        <span className="font-bold">
                            {"GHS"} {Number(totalAmount || 0).toFixed(2)}
                        </span>{" "}
                        was successful
                    </p>
                </div>
                <div className="main">
                    <table>
                        <tr className="px-3">
                            <td>Transaction Type</td>
                            <td className="font-semibold">{details?.gateway?.name || "Card"}</td>
                        </tr>
                        <tr className="px-3">
                            <td>Transaction Reference</td>
                            <td className="font-semibold">{reference || 'N/A'}</td>
                        </tr>
                        <tr className="px-3">
                            <td>Transaction ID</td>
                            <td className="font-semibold">{details?.txnId || 'N/A'}</td>
                        </tr>
                        <tr className="px-3">
                            <td>Amount</td>
                            <td className="font-semibold">{details?.gateway?.currency || "GHS"} {Number(totalAmount || 0).toFixed(2)}</td>
                        </tr>
                        <tr className="px-3">
                            <td>Status</td>
                            <td className="font-semibold text-green-500">{details?.status}</td>
                        </tr>
                        {/* <tr className="px-3">
                            <td>Date</td>
                            <td className="font-semibold">{details?.metrics?.requestTime || moment().format("YYYY-MM-DD")}</td>
                        </tr>
                        <tr className="px-3">
                            <td>Time</td>
                            <td className="font-semibold">{details?.metrics?.requestTime || moment().format("hh:mm A")}</td>
                        </tr> */}
                    </table>
                </div>
                <div>
                    <div className="footer">
                        <a
                            className="bg-[#fff] hover:bg-[#fff] border-[#979797] border-[1px] text-[#979797] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full text-center no-underline"
                            type="button"
                            href={
                                redirectWhenSuccessfulUrl
                                    ? normalizeUrl(redirectWhenSuccessfulUrl)
                                    : 'https://xcelapp.com/'
                            }
                        >
                            Close
                        </a>
                        <button
                            className="bg-[#0095FF] hover:bg-[#0095FFb6] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                            type="button"
                            onClick={() => {
                                toPng(elementRef.current, { cacheBust: false })
                                    .then((dataUrl) => {
                                        const link = document.createElement("a");
                                        link.download = "receipt.png";
                                        link.href = dataUrl;
                                        link.click();
                                    })
                                    .catch((err) => {});
                            }}>
                            Download Receipt
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MasterCardSuccess;